import { Search } from 'lucide-react'
import React, { useMemo } from 'react'
import { toast } from 'sonner'
import z from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { useForm } from 'react-hook-form'
import type { DefaultError } from '@tanstack/react-query'
import type { AccessorColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import PageContainer from '@/components/page-container'
import { Button, buttonVariants } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RtlsEye, RtlsPlus, RtlsReload, RtlsTrashAlt } from '@/icons'

import { useDeleteConfirmation } from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { buildQueryParams, cn } from '@/lib/utils'
import $api from '@/services/api'

const searchParamsSchema = z.object({
  page: fallback(z.number(), 1).default(1),
  limit: fallback(z.number(), 10).default(10),
  keyword: fallback(z.string(), '').default(''),
  roleId: fallback(z.string(), '').default(''),
  status: fallback(z.enum(['Active', 'Inactive', '']), '').default(''),
})

type SearchParamsInput = z.infer<typeof searchParamsSchema>

function RoleFilterField({
  value,
  onChange,
}: {
  value: string | undefined
  onChange: (value: string) => void
}) {
  const { data } = $api.useQuery('get', '/roles', {
    query: { limit: 100 },
  })

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full min-w-48">
        <SelectValue placeholder="Filter by Role" />
      </SelectTrigger>
      <SelectContent>
        {data?.data?.map((role) => (
          <SelectItem key={role.id} value={role.id}>
            {role.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export const Route = createFileRoute('/admin/management/users/')({
  component: UserManagement,
  validateSearch: zodValidator(searchParamsSchema),
})

function UserManagement() {
  const searchParams = Route.useSearch()
  const page = searchParams.page
  const limit = searchParams.limit

  const navigate = Route.useNavigate()
  const queryClient = Route.useRouteContext().queryClient

  const { showDeleteConfirmation } = useDeleteConfirmation()

  const deleteUserMutation = $api.useMutation('delete', '/users/{id}', {
    onSuccess: () => {
      toast.success(`User deleted successfully`)
      queryClient.invalidateQueries({
        queryKey: $api.queryOptions('get', '/users').queryKey,
      })
    },
    onError: (error) => {
      toast.error(`Failed to delete user`, {
        description:
          (error as unknown as DefaultError).message || 'Something went wrong',
      })
    },
  })

  const { data, isPending } = $api.useQuery('get', '/users', {
    params: {
      query: {
        ...buildQueryParams(searchParams),
        page: `${page}`,
        limit: `${limit}`,
      },
    },
  })

  const actions = React.useMemo(
    () => [
      <Link
        to="/admin/management/users/create"
        key="create"
        className={cn('w-full sm:w-auto', buttonVariants())}
        aria-label="Create new user"
        title="Create new user"
      >
        <RtlsPlus aria-hidden="true" /> Add New User
      </Link>,
    ],
    [],
  )

  const form = useForm({
    resolver: zodResolver(searchParamsSchema),
    defaultValues: searchParams,
  })

  const onSubmit = (values: SearchParamsInput) => {
    navigate({
      search: values,
    })
  }

  const handleReset = () => {
    form.reset(searchParamsSchema.parse({}))
    form.handleSubmit(onSubmit)()
  }

  const dataSource = useMemo(() => {
    if (!data?.items) return []
    return data.items.map((user, index: number) => ({
      no: (page - 1) * limit + index + 1,
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role?.name || '-',
      status: user.status,
    }))
  }, [data, page, limit])

  const columns = React.useMemo<
    Array<AccessorColumnDef<(typeof dataSource)[number], any>>
  >(
    () => [
      {
        accessorKey: 'no',
        header: 'NO',

        size: 80,
        meta: {
          cellClassName: 'text-center',
        },
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: 'NAME',
      },
      {
        accessorKey: 'email',
        header: 'EMAIL',
      },
      {
        accessorKey: 'role',
        header: 'ROLE',
      },
      {
        accessorKey: 'status',
        header: 'STATUS',
        size: 100,
        meta: {
          cellClassName: 'text-center',
        },
        cell: (info) => {
          const value = info.getValue() as (typeof dataSource)[number]['status']
          return (
            <Badge
              variant="outline"
              type={value === 'Active' ? 'success' : 'error'}
            >
              {value}
            </Badge>
          )
        },
      },
      {
        id: 'actions',
        accessorKey: 'id',
        header: 'ACTION',
        size: 100,
        meta: {
          headerClassName: 'text-center',
        },
        enableSorting: false,
        enableHiding: false,
        cell: (info) => {
          const userId = info.getValue()
          const rowData = info.row.original
          return (
            <ButtonGroup
              role="group"
              aria-label="User actions"
              className="justify-center"
            >
              <Link
                to="/admin/management/users/$id"
                params={{ id: userId }}
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
                aria-label="View user details"
                title="View user details"
              >
                <RtlsEye className="text-primary size-3.5" aria-hidden="true" />
                <span className="sr-only">View</span>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await showDeleteConfirmation({
                    itemName: 'User',
                    itemDetails: rowData.name,
                    onConfirm: () => {
                      deleteUserMutation.mutate({
                        params: { path: { id: userId } },
                      })
                    },
                  })
                }}
                aria-label="Delete user"
                title="Delete user"
                disabled={deleteUserMutation.isPending}
              >
                <RtlsTrashAlt
                  className="size-3.5 text-neutral-60"
                  aria-hidden="true"
                />
                <span className="sr-only">Delete</span>
              </Button>
            </ButtonGroup>
          )
        },
      },
    ],
    [],
  )

  return (
    <PageContainer title="User Management" actions={actions}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          aria-label="Search users"
          role="search"
        >
          <div className="flex flex-col md:flex-row gap-3">
            <FormField
              control={form.control}
              name="keyword"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        placeholder="Search"
                        className="pr-10"
                        aria-label="Search users by name or email"
                        aria-describedby="search-help"
                      />
                      <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 size-4 text-muted-foreground" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="roleId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RoleFilterField
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full min-w-48">
                        <SelectValue placeholder="Filter by Status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <div
              className="flex flex-col md:flex-row gap-3 "
              role="group"
              aria-label="Search actions"
            >
              <Button
                type="submit"
                loading={isPending}
                aria-label="Apply search filters"
                aria-busy={isPending}
              >
                Apply
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isPending}
                aria-label="Reset search filters"
                aria-disabled={isPending}
              >
                <RtlsReload aria-hidden="true" />
                Reset
              </Button>
            </div>
          </div>
          <div id="search-help" className="sr-only">
            Search for users by name, email, role, or status. Results will
            update when submitted.
          </div>
        </form>
      </Form>
      <DataTable
        columns={columns}
        loading={isPending}
        data={dataSource}
        aria-label="Users table"
        columnPinning={{
          right: ['actions'],
        }}
        pagination={{
          page: Number(page),
          pageSize: Number(limit),
          total: data?.meta.total ?? 0,
          manualPagination: true,
          onChange: (updater) => {
            navigate({
              search: (s) => ({
                ...s,
                page: updater.page,
                limit: updater.pageSize,
              }),
            })
          },
        }}
      />
    </PageContainer>
  )
}
